import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  AUTH_CLEARED_EVENT,
  AUTH_TOKEN_REFRESHED_EVENT,
  AUTH_USER_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  setAuthToken,
} from '../api/axios';
import { AuthUser } from '../services/auth.types';

type AuthState = {
  authenticatedUser: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  userCountry: string | null;
  setAuthenticatedUser: (user: AuthUser | null) => void;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setUserCountry: (country: string | null) => void;
  clearAuth: () => void;
  getUserCountry: () => string | undefined;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authenticatedUser, setAuthenticatedUserState] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [userCountry, setUserCountryState] = useState<string | null>(() => {
    try {
      return localStorage.getItem('user_country');
    } catch (e) {
      return null;
    }
  });

  const [accessToken, setAccessTokenState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  });

  const [refreshToken, setRefreshTokenState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (userCountry) {
        localStorage.setItem('user_country', userCountry);
      } else {
        localStorage.removeItem('user_country');
      }
    } catch (e) {}
  }, [userCountry]);

  useEffect(() => {
    try {
      if (authenticatedUser) {
        localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(authenticatedUser));
      } else {
        localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      }
    } catch (e) {}
  }, [authenticatedUser]);

  useEffect(() => {
    try {
      if (accessToken) {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
        setAuthToken(accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        setAuthToken(null);
      }
    } catch (e) {}
  }, [accessToken]);

  useEffect(() => {
    try {
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      }
    } catch (e) {}
  }, [refreshToken]);

  useEffect(() => {
    const handleTokenRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{
        accessToken?: string | null;
        refreshToken?: string | null;
        user?: AuthUser | null;
      }>).detail;

      if (!detail) return;

      if (detail.accessToken !== undefined) {
        setAccessTokenState(detail.accessToken);
      }
      if (detail.refreshToken !== undefined) {
        setRefreshTokenState(detail.refreshToken);
      }
      if (detail.user !== undefined) {
        setAuthenticatedUserState(detail.user);
      }
    };

    const handleAuthCleared = () => {
      setAuthenticatedUserState(null);
      setAccessTokenState(null);
      setRefreshTokenState(null);
      setUserCountryState(null);
    };

    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefresh);
    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);

    return () => {
      window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefresh);
      window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    };
  }, []);

  const setAuthenticatedUser = (user: AuthUser | null) => {
    setAuthenticatedUserState(user);
  };

  const setAccessToken = (token: string | null) => {
    setAccessTokenState(token);
  };

  const setRefreshToken = (token: string | null) => {
    setRefreshTokenState(token);
  };

  const setUserCountry = (country: string | null) => {
    setUserCountryState(country);
  };

  const getUserCountry = () => {
    return userCountry || authenticatedUser?.country;
  };

  const clearAuth = () => {
    setAuthenticatedUserState(null);
    setAccessTokenState(null);
    setRefreshTokenState(null);
    setUserCountryState(null);
  };

  return (
    <AuthContext.Provider value={{ authenticatedUser, accessToken, refreshToken, userCountry, setAuthenticatedUser, setAccessToken, setRefreshToken, setUserCountry, clearAuth, getUserCountry }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
